/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */
import { FlexCol, FlexRow } from 'sarsaparilla';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import orderBy from 'lodash/orderBy';
import {
    AnchorPageWithSidebarLayout,
    AnchorPageSection,
} from 'shared-ui/components/AnchorPageWithSidebarLayout';
import { InventoryReviewsSection } from 'ui-rating-review';
import List from './List';
import Permits from './Permits';
import Maps from './Maps';
import Contact from './Contact';
import type {
    TreePermitData,
    SeasonData,
    RatingData,
    PermitType,
} from '../../../types/permit';

interface ContentType {
    treePermit: TreePermitData;
    permitTypes: Array<PermitType>;
    seasons: Record<string, SeasonData>;
    rating: RatingData;
    isLoaded: boolean;
}

export default function Content({
    treePermit,
    permitTypes,
    seasons,
    rating,
    isLoaded,
}: ContentType) {
    const ntk = orderBy(
        get(treePermit, 'need_to_know.need_to_know', []).filter(
            (item) => !isNil(item.paragraphs)
        ),
        ['title'],
        ['desc', 'asc']
    );
    const pyt = orderBy(
        get(treePermit, 'planning_your_trip.how_to_plan_your_trip', []).filter(
            (item) => !isNil(item.paragraphs)
        ),
        ['title'],
        ['desc', 'asc']
    );
    const RenderIf = (val: any) => !isEmpty(val) && !isNil(val);
    const numberOfRatings = get(rating, 'number_of_ratings', 1);
    const renderRatingIf = numberOfRatings > 0 && !isEmpty(treePermit);

    return (
        <div className="tp-content">
            <FlexRow>
                <FlexCol lg={12}>
                    <AnchorPageWithSidebarLayout>
                        <AnchorPageSection
                            headingLevel={2}
                            headingAppearance="h5"
                            heading="Fees and Season Dates"
                        >
                            <Permits
                                permits={permitTypes}
                                seasons={seasons}
                                isLoaded={isLoaded}
                            />
                        </AnchorPageSection>
                        {RenderIf(ntk) && (
                            <AnchorPageSection
                                headingLevel={2}
                                headingAppearance="h5"
                                heading="Need to Know"
                            >
                                <List content={ntk} />
                            </AnchorPageSection>
                        )}
                        {RenderIf(treePermit?.maps) && (
                            <AnchorPageSection
                                headingLevel={2}
                                headingAppearance="h5"
                                heading="Maps"
                            >
                                <Maps maps={treePermit?.maps} />
                            </AnchorPageSection>
                        )}
                        {RenderIf(pyt) && (
                            <AnchorPageSection
                                headingLevel={2}
                                headingAppearance="h5"
                                heading="Planning Your Trip"
                            >
                                <List content={pyt} />
                            </AnchorPageSection>
                        )}
                        {RenderIf(treePermit?.contact_us) && (
                            <AnchorPageSection
                                headingLevel={2}
                                headingAppearance="h5"
                                heading="Contact Us"
                            >
                                <Contact content={treePermit?.contact_us} />
                            </AnchorPageSection>
                        )}

                        {renderRatingIf && (
                            <AnchorPageSection
                                headingLevel={2}
                                headingAppearance="h5"
                                heading="Rating and Reviews"
                            >
                                <InventoryReviewsSection
                                    locationId={treePermit?.id}
                                    locationType="Tree Permit"
                                    noHeading={true}
                                />
                            </AnchorPageSection>
                        )}
                    </AnchorPageWithSidebarLayout>
                </FlexCol>
            </FlexRow>
        </div>
    );
}
